import { useEffect, useMemo, useState } from 'react';
import type { FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useParams } from 'react-router-dom';

import { MERCADOS, PLATAFORMAS_AMAZON, formatUsuarioLabel } from '@leadsphere/types';

import { Button } from '../../components/ui/Button.js';
import { CheckboxDropdown } from '../../components/ui/CheckboxDropdown.js';
import { SeccionColapsable } from '../../components/ui/SeccionColapsable.js';
import { useApi } from '../../hooks/useApi.js';
import { useClientesLookup } from '../../hooks/useClientesLookup.js';
import { ApiError } from '../../lib/api.js';
import { ordenarCatalogo } from '../../lib/catalogos.js';
import { toDateInputValue } from '../../lib/dates.js';
import { PAISES } from '../../lib/paises.js';
import type { CatalogoItem, Cliente, ClienteDetalleResponse, Usuario } from './types.js';

type EstadoPermisos = 'no_solicitados' | 'solicitados' | 'autorizados';

interface ContactoForm {
  nombre: string;
  correo: string;
  telefono: string;
  rol: string;
  // Prompt BK-04 — solo relevante para secundarios (un primario siempre se
  // copia en comunicaciones); se envía igual para ambos, el backend/consulta
  // de correos activos ya sabe ignorarlo cuando es_principal=true.
  copiar_en_comunicaciones: boolean;
}

interface PlataformaForm {
  // Bug de plataformas duplicadas: presente = fila existente en BD, se
  // reconcilia por id en el backend; ausente = fila nueva, se inserta.
  id?: string;
  plataforma: string;
  estado_permisos: EstadoPermisos;
  delegar_permisos_a: string[];
  fecha_primer_reporte: string;
  frecuencia_reporte: string;
  responsable: string;
  // Fase 3a (decisiones.md 2026-09-30) — Mercados ISO-2 de esta plataforma;
  // reemplaza a "Permisos (detalle)", que se eliminó.
  mercados: string[];
}

// Fase 3a — orden fijo MX, US, UK, CO, BR, CA (A12).
const MERCADO_OPTIONS = MERCADOS.map(m => ({ value: m, label: m }));
const esPlataformaAmazon = (plataforma: string) =>
  (PLATAFORMAS_AMAZON as readonly string[]).includes(plataforma);

const emptyContacto = (): ContactoForm => ({ nombre: '', correo: '', telefono: '', rol: '', copiar_en_comunicaciones: false });
const emptyPlataforma = (): PlataformaForm => ({
  plataforma: '',
  estado_permisos: 'no_solicitados',
  delegar_permisos_a: [],
  fecha_primer_reporte: '',
  frecuencia_reporte: '',
  responsable: '',
  mercados: [],
});

const ESTADO_PERMISOS_OPTIONS: { value: EstadoPermisos; label: string }[] = [
  { value: 'no_solicitados', label: 'No Solicitados' },
  { value: 'solicitados', label: 'Solicitados' },
  { value: 'autorizados', label: 'Autorizados' },
];

const FRECUENCIA_REPORTE_OPTIONS = [
  { value: 'semanal', label: 'Semanal' },
  { value: 'quincenal', label: 'Quincenal' },
  { value: 'mensual', label: 'Mensual' },
  { value: 'otra', label: 'Otra' },
];

const TIPO_ENTRADA_OPTIONS = [
  { value: 'servicio', label: 'Servicio' },
  { value: 'complemento', label: 'Complemento' },
  { value: 'refacturacion', label: 'Refacturación' },
  { value: 'ads', label: 'Ads' },
  { value: 'otro', label: 'Otro' },
];

const ESTATUS_LABEL: Record<string, string> = {
  activo: 'Activo',
  pausado: 'Pausado',
  pendiente: 'Pendiente',
  fuera_del_servicio: 'Fuera del servicio',
};

const PRIORIDAD_OPTIONS = [
  { value: 'A', label: 'A' },
  { value: 'B', label: 'B' },
  { value: 'C', label: 'C' },
];

// Prompt BK-04 — 6 documentos de "Propuesta", key = columna real en `clientes`.
const PROPUESTA_DOCS: { key: 'doc_plan_trabajo' | 'doc_carta_intencion' | 'doc_propuesta' | 'doc_contrato' | 'doc_sla' | 'doc_nda'; label: string }[] = [
  { key: 'doc_plan_trabajo', label: 'Plan de trabajo' },
  { key: 'doc_carta_intencion', label: 'Carta de intención' },
  { key: 'doc_propuesta', label: 'Propuesta' },
  { key: 'doc_contrato', label: 'Contrato' },
  { key: 'doc_sla', label: 'SLA' },
  { key: 'doc_nda', label: 'NDA' },
];

function FieldError({ message }: { message: string | undefined }) {
  if (!message) return null;
  return <span className="field-error">{message}</span>;
}

export function NuevoCliente() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { get, post, patch } = useApi();
  const { id: clienteId } = useParams<{ id: string }>();
  const esEdicion = Boolean(clienteId);

  // ── Datos del Cliente ──
  const [nombreComercial, setNombreComercial] = useState('');
  const [razonSocial, setRazonSocial] = useState('');
  const [customerType, setCustomerType] = useState('');
  const [level, setLevel] = useState('');
  const [territorio, setTerritorio] = useState('');
  const [paisOperacion, setPaisOperacion] = useState('');
  const [origenLead, setOrigenLead] = useState('');
  const [billingCurrency, setBillingCurrency] = useState('');
  const [giro, setGiro] = useState('');
  const [idioma, setIdioma] = useState('');
  const [fechaInicioServicio, setFechaInicioServicio] = useState('');
  const [estatus, setEstatus] = useState('');
  const [tipoEntrada, setTipoEntrada] = useState('servicio');
  const [clientePrincipalId, setClientePrincipalId] = useState('');
  const [prioridad, setPrioridad] = useState('');
  const [fechaSalida, setFechaSalida] = useState('');

  // ── Comisiones de Ads / Terminación de Contrato ──
  const [comisionesAds, setComisionesAds] = useState(false);
  const [fechaInicioComisionesAds, setFechaInicioComisionesAds] = useState('');
  const [mesesContrato, setMesesContrato] = useState('');
  const [terminacionContrato, setTerminacionContrato] = useState('');

  // Prompt BK-04 — recalcula y SOBREESCRIBE terminacionContrato cada vez que
  // cambia fecha_inicio_servicio o meses_contrato (mientras meses_contrato
  // tenga un valor válido) — sin excepción ni confirmación, aunque ya se
  // hubiera ajustado a mano antes. Si meses_contrato se vacía/es inválido, no
  // toca lo que ya haya en terminacionContrato (deja el ajuste manual).
  useEffect(() => {
    const meses = parseInt(mesesContrato, 10);
    if (!fechaInicioServicio || !mesesContrato || Number.isNaN(meses) || meses <= 0) return;
    const fecha = new Date(`${fechaInicioServicio}T12:00:00`);
    if (Number.isNaN(fecha.getTime())) return;
    fecha.setMonth(fecha.getMonth() + meses);
    setTerminacionContrato(fecha.toISOString().slice(0, 10));
  }, [fechaInicioServicio, mesesContrato]);

  // ── Propuesta ──
  const [docPlanTrabajo, setDocPlanTrabajo] = useState(false);
  const [docCartaIntencion, setDocCartaIntencion] = useState(false);
  const [docPropuesta, setDocPropuesta] = useState(false);
  const [docContrato, setDocContrato] = useState(false);
  const [docSla, setDocSla] = useState(false);
  const [docNda, setDocNda] = useState(false);
  const docPropuestaSetters: Record<(typeof PROPUESTA_DOCS)[number]['key'], [boolean, (v: boolean) => void]> = {
    doc_plan_trabajo:    [docPlanTrabajo, setDocPlanTrabajo],
    doc_carta_intencion: [docCartaIntencion, setDocCartaIntencion],
    doc_propuesta:       [docPropuesta, setDocPropuesta],
    doc_contrato:        [docContrato, setDocContrato],
    doc_sla:             [docSla, setDocSla],
    doc_nda:             [docNda, setDocNda],
  };

  // ── Control Interno ──
  // Prompt BK-04 — multi-KAM: array de usuario_id (antes un solo nombre en
  // texto libre). Reusa CheckboxDropdown, mismo patrón que "Involucrados".
  const [kamIds, setKamIds] = useState<string[]>([]);
  const [carpetaAssets, setCarpetaAssets] = useState('');
  const [involucrados, setInvolucrados] = useState<string[]>([]);
  const [brandRegistry, setBrandRegistry] = useState(false);
  const [brandRegistryUrl, setBrandRegistryUrl] = useState('');
  const [anotaciones, setAnotaciones] = useState('');

  // ── Contactos / Plataformas ──
  // Prompt BK-04 — divididos en dos secciones (primarios/secundarios), cada
  // una con su propio estado — evita reindexar un array mezclado al editar.
  const [contactosPrimarios, setContactosPrimarios] = useState<ContactoForm[]>([]);
  const [contactosSecundarios, setContactosSecundarios] = useState<ContactoForm[]>([]);
  const [plataformasForm, setPlataformasForm] = useState<PlataformaForm[]>([]);

  const [errores, setErrores] = useState<Record<string, string>>({});
  const [apiError, setApiError] = useState('');

  // ── Catálogos y listas de referencia ──
  // Los 4 catálogos (incluido Tier, antes hardcodeado como ['T1','T2','T3'] —
  // por eso los tiers nuevos no aparecían ni con F5) comparten el prefijo de
  // queryKey ['catalogo', tipo] para que CatalogoCard.tsx pueda invalidarlos
  // todos a la vez tras crear/editar/eliminar un ítem.
  const { data: origenLeadOpts } = useQuery({
    queryKey: ['catalogo', 'origen_lead'],
    queryFn: () => get<CatalogoItem[]>('/catalogos/origen_lead'),
  });
  const { data: giroOpts } = useQuery({
    queryKey: ['catalogo', 'giro'],
    queryFn: () => get<CatalogoItem[]>('/catalogos/giro'),
  });
  const { data: plataformaOpts } = useQuery({
    queryKey: ['catalogo', 'plataforma'],
    queryFn: () => get<CatalogoItem[]>('/catalogos/plataforma'),
  });
  const { data: tierOpts } = useQuery({
    queryKey: ['catalogo', 'tier'],
    queryFn: () => get<CatalogoItem[]>('/catalogos/tier'),
  });
  const { data: usuarios } = useQuery({
    queryKey: ['usuarios'],
    queryFn: () => get<Usuario[]>('/usuarios'),
  });
  const { data: clientesLookup } = useClientesLookup({ enabled: tipoEntrada !== 'servicio' });

  // ── Modo edición: cargar cliente existente y precargar el formulario ──
  const { data: clienteExistente, isLoading: cargandoCliente } = useQuery({
    queryKey: ['cliente', clienteId],
    queryFn: () => get<ClienteDetalleResponse>(`/clientes/${clienteId}`),
    enabled: esEdicion,
  });

  useEffect(() => {
    if (!clienteExistente) return;
    setNombreComercial(clienteExistente.nombre_comercial);
    setRazonSocial(clienteExistente.razon_social ?? '');
    setCustomerType(clienteExistente.customer_type);
    setLevel(clienteExistente.level ?? '');
    setTerritorio(clienteExistente.territorio ?? '');
    setPaisOperacion(clienteExistente.pais_operacion ?? '');
    setOrigenLead(clienteExistente.origen_lead ?? '');
    setBillingCurrency(clienteExistente.billing_currency);
    setGiro(clienteExistente.giro ?? '');
    setIdioma(clienteExistente.idioma);
    setFechaInicioServicio(toDateInputValue(clienteExistente.fecha_inicio_servicio));
    setTipoEntrada(clienteExistente.tipo_entrada);
    setClientePrincipalId(clienteExistente.cliente_principal_id ?? '');
    // estatus se precarga solo para saber si el estatus ACTUAL es 'pendiente'
    // (controla qué asteriscos mostrar) — el <select> de Estatus permanece
    // oculto en edición, ver comentario más abajo.
    setEstatus(clienteExistente.estatus);
    setPrioridad(clienteExistente.prioridad ?? '');
    setFechaSalida(clienteExistente.fecha_salida ?? '');
    setComisionesAds(clienteExistente.comisiones_ads);
    setFechaInicioComisionesAds(toDateInputValue(clienteExistente.fecha_inicio_comisiones_ads));
    setTerminacionContrato(toDateInputValue(clienteExistente.terminacion_contrato));
    setDocPlanTrabajo(clienteExistente.doc_plan_trabajo);
    setDocCartaIntencion(clienteExistente.doc_carta_intencion);
    setDocPropuesta(clienteExistente.doc_propuesta);
    setDocContrato(clienteExistente.doc_contrato);
    setDocSla(clienteExistente.doc_sla);
    setDocNda(clienteExistente.doc_nda);
    setKamIds(clienteExistente.kams.map(k => k.id));
    setCarpetaAssets(clienteExistente.carpeta_assets ?? '');
    setInvolucrados(clienteExistente.involucrados);
    setBrandRegistry(clienteExistente.brand_registry);
    setBrandRegistryUrl(clienteExistente.brand_registry_url ?? '');
    setAnotaciones(clienteExistente.anotaciones ?? '');
    const contactoForm = (c: NonNullable<typeof clienteExistente.contactos>[number]): ContactoForm => ({
      nombre:                   c.nombre,
      correo:                   c.correo ?? '',
      telefono:                 c.telefono ?? '',
      rol:                      c.rol ?? '',
      copiar_en_comunicaciones: c.copiar_en_comunicaciones,
    });
    setContactosPrimarios((clienteExistente.contactos ?? []).filter(c => c.es_principal).map(contactoForm));
    setContactosSecundarios((clienteExistente.contactos ?? []).filter(c => !c.es_principal).map(contactoForm));
    setPlataformasForm(
      clienteExistente.plataformas.map(p => ({
        id: p.id,
        plataforma: p.plataforma,
        estado_permisos: p.estado_permisos as EstadoPermisos,
        delegar_permisos_a: p.delegar_permisos_a,
        fecha_primer_reporte: toDateInputValue(p.fecha_primer_reporte),
        frecuencia_reporte: p.frecuencia_reporte ?? '',
        responsable: p.responsable ?? '',
        mercados: p.mercados ?? [],
      })),
    );
  }, [clienteExistente]);

  // Solo rol='kam' a propósito (no 'ads_manager'): es el KAM asignado de la
  // cuenta, y el backend exige rol='kam' en kam_ids (validarUsuariosSonKam).
  const kams = useMemo(() => (usuarios ?? []).filter(u => u.rol === 'kam' && u.activo), [usuarios]);
  const involucradosOpts = useMemo(() => (usuarios ?? []).filter(u => u.activo), [usuarios]);
  // Quienes gestionan accesos de plataformas — usados como Responsable del reporte en Plataformas y Accesos
  const gestionAccesosOpts = useMemo(
    // Prompt BJ-04 — 'marketing' agregado: equivalente exacto de 'digital'.
    // 'ads_manager' agregado: equivalente exacto de 'kam'.
    () => (usuarios ?? []).filter(u => ['root', 'kam', 'ads_manager', 'digital', 'marketing'].includes(u.rol) && u.activo),
    [usuarios],
  );

  const origenLeadOrdenado = useMemo(() => ordenarCatalogo(origenLeadOpts ?? [], 'origen_lead'), [origenLeadOpts]);
  const giroOrdenado = useMemo(() => ordenarCatalogo(giroOpts ?? [], 'giro'), [giroOpts]);
  const plataformaOrdenada = useMemo(() => ordenarCatalogo(plataformaOpts ?? [], 'plataforma'), [plataformaOpts]);
  const tierOrdenado = useMemo(() => ordenarCatalogo(tierOpts ?? [], 'tier'), [tierOpts]);

  const guardarMutation = useMutation({
    // Prompt BK-04 — estatus ahora viaja DENTRO del payload de creación (antes
    // se creaba siempre como 'activo' y se corregía con un PATCH /:id/estatus
    // aparte) — necesario para que la validación condicional del backend
    // (clienteSchema) sepa, desde el insert, si el registro es un borrador
    // "pendiente" o exige el esquema completo.
    mutationFn: (payload: Record<string, unknown>) =>
      esEdicion ? patch<Cliente>(`/clientes/${clienteId}`, payload) : post<Cliente>('/clientes', payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['clientes'] });
      if (esEdicion) {
        queryClient.invalidateQueries({ queryKey: ['cliente', clienteId] });
        navigate(`/clientes/${clienteId}`);
      } else {
        navigate('/clientes');
      }
    },
    onError: (err: unknown) => {
      setApiError(err instanceof ApiError ? err.message : 'Ocurrió un error al guardar el cliente. Intenta de nuevo.');
    },
  });

  // Prompt BK-04 — solo nombre_comercial es obligatorio cuando estatus es
  // "pendiente" (borrador); el resto exige el esquema completo. Espejo del
  // superRefine de clienteSchema en @leadsphere/types — la validación real
  // y autoritativa sigue siendo la del backend, esto es solo feedback
  // inmediato en UI. fecha_inicio_servicio queda EXCLUIDA siempre.
  function validar(): Record<string, string> {
    const errs: Record<string, string> = {};
    const requiereCompleto = estatus !== 'pendiente';

    if (!nombreComercial.trim()) errs['nombre_comercial'] = 'Requerido';
    if (!esEdicion && !estatus) errs['estatus'] = 'Requerido';

    if (requiereCompleto) {
      if (!razonSocial.trim()) errs['razon_social'] = 'Requerido';
      if (!level) errs['level'] = 'Requerido';
      if (!territorio) errs['territorio'] = 'Requerido';
      if (!giro) errs['giro'] = 'Requerido';
      if (!kamIds.length) errs['kam_ids'] = 'Requerido (al menos un KAM)';
    }
    if (territorio === 'extranjero' && !paisOperacion.trim()) errs['pais_operacion'] = 'Requerido cuando el territorio es Extranjero';
    if (!tipoEntrada) errs['tipo_entrada'] = 'Requerido';
    if (tipoEntrada !== 'servicio' && !clientePrincipalId) {
      errs['cliente_principal_id'] = 'Requerido cuando el tipo de entrada no es Servicio';
    }

    // F.6 §6 — no se puede agregar la misma plataforma dos veces en un cliente
    const nombresPlataforma = plataformasForm.map(p => p.plataforma.trim().toLowerCase()).filter(Boolean);
    const duplicada = nombresPlataforma.find((v, i) => nombresPlataforma.indexOf(v) !== i);
    if (duplicada) {
      const original = plataformasForm.find(p => p.plataforma.trim().toLowerCase() === duplicada)?.plataforma ?? duplicada;
      errs['plataformas'] = `La plataforma "${original}" ya está agregada — no puedes repetirla.`;
    }

    return errs;
  }

  function buildPayload(): Record<string, unknown> {
    const base: Record<string, unknown> = {
      nombre_comercial: nombreComercial.trim(),
      tipo_entrada: tipoEntrada,
      ...(!esEdicion ? { estatus } : {}),
      ...(razonSocial.trim() ? { razon_social: razonSocial.trim() } : {}),
      ...(tipoEntrada !== 'servicio' ? { cliente_principal_id: clientePrincipalId } : {}),
      ...(customerType ? { customer_type: customerType } : {}),
      ...(level ? { level } : {}),
      ...(territorio ? { territorio } : {}),
      ...(territorio === 'extranjero' ? { pais_operacion: paisOperacion.trim() } : {}),
      ...(origenLead ? { origen_lead: origenLead } : {}),
      ...(billingCurrency ? { billing_currency: billingCurrency } : {}),
      ...(giro ? { giro } : {}),
      ...(idioma ? { idioma } : {}),
      ...(fechaInicioServicio ? { fecha_inicio_servicio: fechaInicioServicio } : {}),
      prioridad: prioridad || undefined,
      comisiones_ads: comisionesAds,
      ...(comisionesAds && fechaInicioComisionesAds ? { fecha_inicio_comisiones_ads: fechaInicioComisionesAds } : {}),
      ...(terminacionContrato ? { terminacion_contrato: terminacionContrato } : {}),
      doc_plan_trabajo: docPlanTrabajo,
      doc_carta_intencion: docCartaIntencion,
      doc_propuesta: docPropuesta,
      doc_contrato: docContrato,
      doc_sla: docSla,
      doc_nda: docNda,
      kam_ids: kamIds,
      involucrados,
      brand_registry: brandRegistry,
      ...(brandRegistry && brandRegistryUrl.trim() ? { brand_registry_url: brandRegistryUrl.trim() } : {}),
      ...(carpetaAssets.trim() ? { carpeta_assets: carpetaAssets.trim() } : {}),
      ...(anotaciones.trim() ? { anotaciones: anotaciones.trim() } : {}),
    };

    // F.6 §2: actualizarClienteSchema ahora también acepta plataformas/contactos
    // — en edición, el array completo reemplaza lo que ya existía (ver
    // reemplazarPlataformas() en el service); en alta, los crea inline.
    return {
      ...base,
      plataformas: plataformasForm
        .filter(p => p.plataforma)
        .map(p => ({
          ...(p.id ? { id: p.id } : {}),
          plataforma: p.plataforma,
          estado_permisos: p.estado_permisos,
          ...(p.delegar_permisos_a.length ? { delegar_permisos_a: p.delegar_permisos_a } : {}),
          ...(p.fecha_primer_reporte ? { fecha_primer_reporte: p.fecha_primer_reporte } : {}),
          ...(p.frecuencia_reporte ? { frecuencia_reporte: p.frecuencia_reporte } : {}),
          ...(p.responsable ? { responsable: p.responsable } : {}),
          // Siempre se manda: [] = el usuario quitó todos los mercados.
          mercados: p.mercados,
        })),
      // Prompt BK-04 — un único array `contactos` para el backend (distingue
      // primario/secundario por `es_principal`), armado a partir de las DOS
      // secciones separadas del formulario.
      contactos: [
        ...contactosPrimarios.filter(c => c.nombre.trim()).map(c => ({
          nombre: c.nombre.trim(),
          ...(c.correo.trim() ? { correo: c.correo.trim() } : {}),
          ...(c.telefono.trim() ? { telefono: c.telefono.trim() } : {}),
          ...(c.rol.trim() ? { rol: c.rol.trim() } : {}),
          es_principal: true,
        })),
        ...contactosSecundarios.filter(c => c.nombre.trim()).map(c => ({
          nombre: c.nombre.trim(),
          ...(c.correo.trim() ? { correo: c.correo.trim() } : {}),
          ...(c.telefono.trim() ? { telefono: c.telefono.trim() } : {}),
          ...(c.rol.trim() ? { rol: c.rol.trim() } : {}),
          es_principal: false,
          copiar_en_comunicaciones: c.copiar_en_comunicaciones,
        })),
      ],
    };
  }

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    const errs = validar();
    setErrores(errs);
    if (Object.keys(errs).length > 0) return;
    setApiError('');
    guardarMutation.mutate(buildPayload());
  }

  function updateContactoPrimario(index: number, changes: Partial<ContactoForm>) {
    setContactosPrimarios(rows => rows.map((row, i) => (i === index ? { ...row, ...changes } : row)));
  }

  function updateContactoSecundario(index: number, changes: Partial<ContactoForm>) {
    setContactosSecundarios(rows => rows.map((row, i) => (i === index ? { ...row, ...changes } : row)));
  }

  function updatePlataforma(index: number, changes: Partial<PlataformaForm>) {
    setPlataformasForm(rows => rows.map((row, i) => (i === index ? { ...row, ...changes } : row)));
  }

  if (esEdicion && cargandoCliente) {
    return (
      <div className="nc-wrap">
        <p style={{ color: 'var(--muted)' }}>Cargando cliente…</p>
      </div>
    );
  }

  // Prompt BK-04 — controla qué asteriscos se muestran (Nombre Comercial
  // siempre es obligatorio; el resto solo cuando estatus !== 'pendiente').
  // Espejo de la misma condición en validar().
  const requiereCompleto = estatus !== 'pendiente';

  return (
    <div className="nc-wrap">
      <Link to={esEdicion ? `/clientes/${clienteId}` : '/clientes'} className="back-btn">
        {esEdicion ? '← Volver al cliente' : '← Volver a clientes'}
      </Link>

      <div style={{ marginBottom: 22 }}>
        <div className="nc-title">{esEdicion ? 'Editar Cliente' : 'Nuevo Cliente'}</div>
        <div className="nc-sub">
          {esEdicion ? 'Actualiza la información de este cliente' : 'Completa la información para dar de alta un cliente'}
        </div>
        {esEdicion && (
          <div className="nc-sub" style={{ marginTop: 4 }}>
            El estatus se cambia desde el detalle del cliente, no desde este formulario.
          </div>
        )}
      </div>

      {apiError && <div className="form-error-banner">{apiError}</div>}

      <form onSubmit={handleSubmit}>
        <SeccionColapsable title="Datos del Cliente">
          <div className="f2">
            <div className="fld">
              <label>Nombre Comercial *</label>
              <input
                type="text"
                placeholder="Ej: Grisi"
                value={nombreComercial}
                className={errores['nombre_comercial'] ? 'has-error' : ''}
                onChange={e => setNombreComercial(e.target.value)}
              />
              <FieldError message={errores['nombre_comercial']} />
            </div>
            <div className="fld">
              <label>Razón Social{requiereCompleto && ' *'}</label>
              <input
                type="text"
                placeholder="Ej: Grisi Hnos S.A. de C.V."
                value={razonSocial}
                className={errores['razon_social'] ? 'has-error' : ''}
                onChange={e => setRazonSocial(e.target.value)}
              />
              <FieldError message={errores['razon_social']} />
            </div>
            <div className="fld">
              <label>Tipo de Cliente</label>
              <select value={customerType} onChange={e => setCustomerType(e.target.value)}>
                <option value="">Seleccionar</option>
                <option value="empresa">Empresa</option>
                <option value="persona_fisica">Persona Física</option>
              </select>
            </div>
            <div className="fld">
              <label>Nivel / Tier{requiereCompleto && ' *'}</label>
              <select value={level} className={errores['level'] ? 'has-error' : ''} onChange={e => setLevel(e.target.value)}>
                <option value="">Seleccionar</option>
                {tierOrdenado.map(o => (
                  <option key={o.id} value={o.valor}>
                    {o.valor}
                  </option>
                ))}
              </select>
              <FieldError message={errores['level']} />
            </div>
            <div className="fld">
              <label>Territorio{requiereCompleto && ' *'}</label>
              <select
                value={territorio}
                className={errores['territorio'] ? 'has-error' : ''}
                onChange={e => setTerritorio(e.target.value)}
              >
                <option value="">Seleccionar</option>
                <option value="nacional">Nacional</option>
                <option value="extranjero">Extranjero</option>
              </select>
              <FieldError message={errores['territorio']} />
            </div>
            {territorio === 'extranjero' && (
              <div className="fld">
                <label>País de Operación *</label>
                <select
                  value={paisOperacion}
                  className={errores['pais_operacion'] ? 'has-error' : ''}
                  onChange={e => setPaisOperacion(e.target.value)}
                >
                  <option value="">Seleccionar</option>
                  {PAISES.map(pais => (
                    <option key={pais} value={pais}>
                      {pais}
                    </option>
                  ))}
                </select>
                <FieldError message={errores['pais_operacion']} />
              </div>
            )}
            <div className="fld">
              <label>Origen del Lead</label>
              <select value={origenLead} onChange={e => setOrigenLead(e.target.value)}>
                <option value="">Seleccionar</option>
                {origenLeadOrdenado.map(o => (
                  <option key={o.id} value={o.valor}>
                    {o.valor}
                  </option>
                ))}
              </select>
            </div>
            <div className="fld">
              <label>Moneda de Facturación</label>
              <select value={billingCurrency} onChange={e => setBillingCurrency(e.target.value)}>
                <option value="">Seleccionar</option>
                <option value="MXN">MXN</option>
                <option value="USD">USD</option>
                <option value="EUR">EUR</option>
              </select>
            </div>
            <div className="fld">
              <label>Giro{requiereCompleto && ' *'}</label>
              <select value={giro} className={errores['giro'] ? 'has-error' : ''} onChange={e => setGiro(e.target.value)}>
                <option value="">Seleccionar</option>
                {giroOrdenado.map(o => (
                  <option key={o.id} value={o.valor}>
                    {o.valor}
                  </option>
                ))}
              </select>
              <FieldError message={errores['giro']} />
            </div>
            <div className="fld">
              <label>Idioma</label>
              <select value={idioma} onChange={e => setIdioma(e.target.value)}>
                <option value="">Seleccionar</option>
                <option value="espanol">Español</option>
                <option value="ingles">Inglés</option>
                <option value="portugues">Portugués</option>
              </select>
            </div>
            <div className="fld">
              {/* Prompt BK-04 — asterisco removido de forma permanente: opcional
                  en cualquier estatus, reemplaza el concepto de "Inicio de Contrato". */}
              <label>Fecha Inicio Servicio</label>
              <input
                type="date"
                value={fechaInicioServicio}
                onChange={e => setFechaInicioServicio(e.target.value)}
              />
            </div>
            {fechaInicioServicio && (
              <div className="fld">
                <label>No. de meses de contrato</label>
                <input
                  type="number"
                  min={1}
                  step={1}
                  placeholder="Ej: 12"
                  value={mesesContrato}
                  onChange={e => setMesesContrato(e.target.value)}
                />
              </div>
            )}
            <div className="fld">
              <label>Terminación de Contrato</label>
              <input type="date" value={terminacionContrato} onChange={e => setTerminacionContrato(e.target.value)} />
            </div>
            <div className="fld">
              <label>Estatus{!esEdicion && ' *'}</label>
              {esEdicion ? (
                <>
                  <input type="text" value={ESTATUS_LABEL[estatus] ?? estatus} disabled />
                  {fechaSalida && (
                    <span style={{ fontSize: 11.5, color: 'var(--muted)' }}>Fecha de salida: {toDateInputValue(fechaSalida)}</span>
                  )}
                </>
              ) : (
                <select value={estatus} className={errores['estatus'] ? 'has-error' : ''} onChange={e => setEstatus(e.target.value)}>
                  <option value="">Seleccionar</option>
                  <option value="activo">Activo</option>
                  <option value="pausado">Pausado</option>
                  <option value="pendiente">Pendiente</option>
                  <option value="fuera_del_servicio">Fuera del servicio</option>
                </select>
              )}
              <FieldError message={errores['estatus']} />
            </div>
          </div>
        </SeccionColapsable>

        <SeccionColapsable title="Control Interno">
          <div className="f2">
            <div className="fld">
              {/* Prompt BK-04 — multi-KAM: reusa CheckboxDropdown, mismo patrón
                  que "Involucrados" abajo. */}
              <label>KAM Asignado{requiereCompleto && ' *'}</label>
              <CheckboxDropdown
                options={kams.map(u => ({ value: u.id, label: u.puesto ? `${u.nombre} (${formatUsuarioLabel(u)})` : u.nombre }))}
                selected={kamIds}
                onChange={setKamIds}
                placeholder="Seleccionar KAM(s)..."
              />
              <FieldError message={errores['kam_ids']} />
            </div>
            <div className="fld">
              <label>Carpeta de Assets</label>
              <input
                type="text"
                placeholder="URL de carpeta compartida"
                value={carpetaAssets}
                onChange={e => setCarpetaAssets(e.target.value)}
              />
            </div>
            <div className="fld s2">
              <label>Involucrados (copiados en comunicaciones)</label>
              <CheckboxDropdown
                options={involucradosOpts.map(u => ({ value: u.correo, label: u.nombre }))}
                selected={involucrados}
                onChange={setInvolucrados}
                placeholder="Seleccionar involucrados..."
              />
            </div>
            <div className="fld s2">
              <div className="check-row">
                <input
                  type="checkbox"
                  id="nc-br"
                  checked={brandRegistry}
                  onChange={e => setBrandRegistry(e.target.checked)}
                />
                <label htmlFor="nc-br">Tiene Brand Registry</label>
              </div>
            </div>
            {brandRegistry && (
              <div className="fld" style={{ gridColumn: 2 }}>
                <label>URL Brand Registry</label>
                <input
                  type="text"
                  placeholder="https://..."
                  value={brandRegistryUrl}
                  onChange={e => setBrandRegistryUrl(e.target.value)}
                />
              </div>
            )}
          </div>
        </SeccionColapsable>

        <SeccionColapsable title="Contactos primarios">
          <table className="ctbl">
            <thead>
              <tr>
                <th>Nombre</th>
                <th>Correo</th>
                <th>Teléfono</th>
                <th>Rol</th>
                <th style={{ width: 40 }} />
              </tr>
            </thead>
            <tbody>
              {contactosPrimarios.map((c, i) => (
                <tr key={i}>
                  <td>
                    <input
                      type="text"
                      placeholder="Ej: Juan Pérez"
                      value={c.nombre}
                      onChange={e => updateContactoPrimario(i, { nombre: e.target.value })}
                    />
                  </td>
                  <td>
                    <input
                      type="email"
                      placeholder="correo@empresa.com"
                      value={c.correo}
                      onChange={e => updateContactoPrimario(i, { correo: e.target.value })}
                    />
                  </td>
                  <td>
                    <input
                      type="text"
                      placeholder="55 1234 5678"
                      value={c.telefono}
                      onChange={e => updateContactoPrimario(i, { telefono: e.target.value })}
                    />
                  </td>
                  <td>
                    <input
                      type="text"
                      placeholder="Ej: Gerente de Compras"
                      value={c.rol}
                      onChange={e => updateContactoPrimario(i, { rol: e.target.value })}
                    />
                  </td>
                  <td>
                    <button
                      type="button"
                      className="trash-btn"
                      aria-label="Eliminar contacto"
                      onClick={() => setContactosPrimarios(rows => rows.filter((_, idx) => idx !== i))}
                    >
                      ✕
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <div style={{ marginTop: 13 }}>
            <Button variant="secondary" size="sm" type="button" onClick={() => setContactosPrimarios(rows => [...rows, emptyContacto()])}>
              + Agregar Contacto Primario
            </Button>
          </div>
        </SeccionColapsable>

        <SeccionColapsable title="Contactos secundarios">
          <table className="ctbl">
            <thead>
              <tr>
                <th>Nombre</th>
                <th>Correo</th>
                <th>Teléfono</th>
                <th>Rol</th>
                <th>Copiar en comunicaciones</th>
                <th style={{ width: 40 }} />
              </tr>
            </thead>
            <tbody>
              {contactosSecundarios.map((c, i) => (
                <tr key={i}>
                  <td>
                    <input
                      type="text"
                      placeholder="Ej: Juan Pérez"
                      value={c.nombre}
                      onChange={e => updateContactoSecundario(i, { nombre: e.target.value })}
                    />
                  </td>
                  <td>
                    <input
                      type="email"
                      placeholder="correo@empresa.com"
                      value={c.correo}
                      onChange={e => updateContactoSecundario(i, { correo: e.target.value })}
                    />
                  </td>
                  <td>
                    <input
                      type="text"
                      placeholder="55 1234 5678"
                      value={c.telefono}
                      onChange={e => updateContactoSecundario(i, { telefono: e.target.value })}
                    />
                  </td>
                  <td>
                    <input
                      type="text"
                      placeholder="Ej: Gerente de Compras"
                      value={c.rol}
                      onChange={e => updateContactoSecundario(i, { rol: e.target.value })}
                    />
                  </td>
                  <td>
                    <input
                      type="checkbox"
                      checked={c.copiar_en_comunicaciones}
                      onChange={e => updateContactoSecundario(i, { copiar_en_comunicaciones: e.target.checked })}
                    />
                  </td>
                  <td>
                    <button
                      type="button"
                      className="trash-btn"
                      aria-label="Eliminar contacto"
                      onClick={() => setContactosSecundarios(rows => rows.filter((_, idx) => idx !== i))}
                    >
                      ✕
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <div style={{ marginTop: 13 }}>
            <Button variant="secondary" size="sm" type="button" onClick={() => setContactosSecundarios(rows => [...rows, emptyContacto()])}>
              + Agregar Contacto Secundario
            </Button>
          </div>
        </SeccionColapsable>

        <SeccionColapsable title="Plataformas y Accesos">
          <FieldError message={errores['plataformas']} />
          <div>
            {plataformasForm.map((p, i) => (
              <div className="plat-card" key={i}>
                <div className="plat-card-hdr">
                  <span className="plat-card-title">Plataforma {i + 1}</span>
                  <button
                    type="button"
                    className="trash-btn"
                    aria-label="Eliminar plataforma"
                    onClick={() => setPlataformasForm(rows => rows.filter((_, idx) => idx !== i))}
                  >
                    ✕
                  </button>
                </div>
                <div className="g3">
                  <div className="fld">
                    <label>Plataforma</label>
                    <select value={p.plataforma} onChange={e => updatePlataforma(i, { plataforma: e.target.value })}>
                      <option value="">Seleccionar</option>
                      {plataformaOrdenada.map(o => (
                        <option key={o.id} value={o.valor}>
                          {o.valor}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="fld">
                    <label>Estado de Permisos</label>
                    <select
                      value={p.estado_permisos}
                      onChange={e => updatePlataforma(i, { estado_permisos: e.target.value as EstadoPermisos })}
                    >
                      {ESTADO_PERMISOS_OPTIONS.map(o => (
                        <option key={o.value} value={o.value}>
                          {o.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="fld">
                    <label>Permisos Solicitados Para</label>
                    <CheckboxDropdown
                      options={involucradosOpts.map(u => ({ value: u.nombre, label: u.nombre }))}
                      selected={p.delegar_permisos_a}
                      onChange={values => updatePlataforma(i, { delegar_permisos_a: values })}
                      placeholder="Seleccionar..."
                    />
                  </div>
                  <div className="fld">
                    <label>Fecha Primer Reporte</label>
                    <input
                      type="date"
                      value={p.fecha_primer_reporte}
                      onChange={e => updatePlataforma(i, { fecha_primer_reporte: e.target.value })}
                    />
                  </div>
                  <div className="fld">
                    <label>Recurrencia</label>
                    <select
                      value={p.frecuencia_reporte}
                      onChange={e => updatePlataforma(i, { frecuencia_reporte: e.target.value })}
                    >
                      <option value="">Seleccionar</option>
                      {FRECUENCIA_REPORTE_OPTIONS.map(o => (
                        <option key={o.value} value={o.value}>
                          {o.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="fld">
                    <label>Responsable del reporte</label>
                    <select value={p.responsable} onChange={e => updatePlataforma(i, { responsable: e.target.value })}>
                      <option value="">Seleccionar</option>
                      {gestionAccesosOpts.map(u => (
                        <option key={u.id} value={u.nombre}>
                          {u.nombre}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="fld">
                    <label>Mercados</label>
                    <CheckboxDropdown
                      options={MERCADO_OPTIONS}
                      selected={p.mercados}
                      onChange={values => updatePlataforma(i, { mercados: values })}
                      placeholder="Selecciona mercados..."
                    />
                    {esPlataformaAmazon(p.plataforma) && p.mercados.length === 0 && (
                      <span className="char-cnt">Sin mercados, esta plataforma no aparece en Secretos.</span>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
          <Button
            variant="secondary"
            size="sm"
            type="button"
            onClick={() => setPlataformasForm(rows => [...rows, emptyPlataforma()])}
          >
            + Agregar Plataforma
          </Button>
        </SeccionColapsable>

        <SeccionColapsable title="Propuesta">
          <div className="f2">
            <div className="fld">
              <label>Prioridad</label>
              <select value={prioridad} onChange={e => setPrioridad(e.target.value)}>
                <option value="">Sin prioridad</option>
                {PRIORIDAD_OPTIONS.map(o => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="fld s2">
              <label>Anotaciones</label>
              <textarea
                rows={3}
                maxLength={500}
                placeholder="Notas internas sobre el cliente..."
                value={anotaciones}
                onChange={e => setAnotaciones(e.target.value)}
              />
              <span className="char-cnt">{anotaciones.length}/500</span>
            </div>
            {PROPUESTA_DOCS.map(doc => {
              const [checked, setChecked] = docPropuestaSetters[doc.key];
              return (
                <div className="fld" key={doc.key}>
                  <div className="check-row">
                    <input
                      type="checkbox"
                      id={`nc-${doc.key}`}
                      checked={checked}
                      onChange={e => setChecked(e.target.checked)}
                    />
                    <label htmlFor={`nc-${doc.key}`}>{doc.label}</label>
                  </div>
                </div>
              );
            })}
          </div>
        </SeccionColapsable>

        <SeccionColapsable title="Configuración avanzada" defaultOpen={false}>
          <div className="f2">
            <div className="fld">
              <label>Tipo de Entrada *</label>
              <select
                value={tipoEntrada}
                className={errores['tipo_entrada'] ? 'has-error' : ''}
                onChange={e => setTipoEntrada(e.target.value)}
              >
                {TIPO_ENTRADA_OPTIONS.map(o => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
              <FieldError message={errores['tipo_entrada']} />
            </div>
            <div className="fld">
              <div className="check-row">
                <input
                  type="checkbox"
                  id="nc-comisiones-ads"
                  checked={comisionesAds}
                  onChange={e => setComisionesAds(e.target.checked)}
                />
                <label htmlFor="nc-comisiones-ads">Comisiones de Ads</label>
              </div>
            </div>
            {comisionesAds && (
              <div className="fld">
                <label>Fecha de inicio Comisiones Ads</label>
                <input
                  type="date"
                  value={fechaInicioComisionesAds}
                  onChange={e => setFechaInicioComisionesAds(e.target.value)}
                />
              </div>
            )}
            {tipoEntrada !== 'servicio' && (
              <div className="fld">
                <label>Cliente Principal *</label>
                <select
                  value={clientePrincipalId}
                  className={errores['cliente_principal_id'] ? 'has-error' : ''}
                  onChange={e => setClientePrincipalId(e.target.value)}
                >
                  <option value="">Seleccionar</option>
                  {(clientesLookup?.data ?? []).map(c => (
                    <option key={c.id} value={c.id}>
                      {c.nombre_comercial}
                    </option>
                  ))}
                </select>
                <FieldError message={errores['cliente_principal_id']} />
              </div>
            )}
          </div>
        </SeccionColapsable>

        <div className="nc-actions">
          <Button variant="secondary" type="button" onClick={() => navigate(esEdicion ? `/clientes/${clienteId}` : '/clientes')}>
            Cancelar
          </Button>
          <Button type="submit" disabled={guardarMutation.isPending}>
            {guardarMutation.isPending ? 'Guardando…' : esEdicion ? 'Guardar Cambios' : 'Guardar Cliente'}
          </Button>
        </div>
      </form>
    </div>
  );
}
